import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, FileSpreadsheet, RefreshCw, RotateCcw, Search, ShieldCheck, WalletCards, XCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminStorePaymentReconciliation,
  AdminStorePaymentReconciliationPayment,
  AdminUser,
  exportAdminStorePaymentReconciliation,
  getAdminStorePaymentReconciliation,
  getCurrentAdmin,
  logout,
  refundAdminStorePayment,
  reviewAdminStorePayment,
  voidAdminStorePayment,
} from "./api";
import Pagination from "./components/Pagination";

const year = new Date().getFullYear();
const defaultDateFrom = `${year}-01-01`;
const defaultDateTo = `${year}-12-31`;

function money(value?: number | null) {
  return `NT$ ${Number(value || 0).toLocaleString("zh-TW")}`;
}

function StatCard({ icon, label, tone = "default", value }: { icon: ReactNode; label: string; tone?: "default" | "good" | "warning" | "danger"; value: string }) {
  const toneClass =
    tone === "danger"
      ? "border-[#f2c9c6] bg-[#fff8f7]"
      : tone === "warning"
        ? "border-[#ead8ad] bg-[#fff9ed]"
        : tone === "good"
          ? "border-[#b9dec5] bg-[#f0faf4]"
          : "border-[#dfe6e1] bg-white";
  return (
    <div className={`rounded-md border p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)] ${toneClass}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold text-[#69776f]">{label}</p>
          <p className="mt-2 break-words text-2xl font-extrabold text-[#203d32]">{value}</p>
        </div>
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white text-[#315746]">{icon}</span>
      </div>
    </div>
  );
}

function statusTone(status: string, isAbnormal: boolean) {
  if (status === "Reviewed") return "bg-[#e9f4ec] text-[#245438]";
  if (status === "Exception" || isAbnormal) return "bg-[#fdebea] text-[#9a2d2d]";
  if (status === "Matched") return "bg-[#eef3f8] text-[#255071]";
  return "bg-[#fff6df] text-[#7a5610]";
}

export default function AdminStorePaymentReconciliation() {
  const navigate = useNavigate();
  const [user, setUser] = useState<AdminUser | null>(null);
  const [report, setReport] = useState<AdminStorePaymentReconciliation | null>(null);
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [reconciliationStatus, setReconciliationStatus] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState<"success" | "error">("error");
  const [editing, setEditing] = useState<AdminStorePaymentReconciliationPayment | null>(null);
  const [editStatus, setEditStatus] = useState("Matched");
  const [editNote, setEditNote] = useState("");
  const [editAbnormal, setEditAbnormal] = useState(false);
  const [editAbnormalNote, setEditAbnormalNote] = useState("");
  const [editReviewNote, setEditReviewNote] = useState("");
  const [voiding, setVoiding] = useState<AdminStorePaymentReconciliationPayment | null>(null);
  const [voidReason, setVoidReason] = useState("");
  const [refunding, setRefunding] = useState<AdminStorePaymentReconciliationPayment | null>(null);
  const [refundAmount, setRefundAmount] = useState(0);
  const [refundReason, setRefundReason] = useState("");

  const allSelected = useMemo(() => report ? locationIds.length === 0 || locationIds.length === report.Locations.length : false, [locationIds.length, report]);

  async function load(filters: Partial<{ DateFrom: string; DateTo: string; LocationIds: string[]; PaymentMethod: string; ReconciliationStatus: string; Keyword: string; Page: number; PerPage: number }> = {}) {
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentAdmin();
      setUser(me.User);
      const data = await getAdminStorePaymentReconciliation({
        DateFrom: filters.DateFrom ?? dateFrom,
        DateTo: filters.DateTo ?? dateTo,
        LocationIds: filters.LocationIds ?? locationIds,
        PaymentMethod: filters.PaymentMethod ?? paymentMethod,
        ReconciliationStatus: filters.ReconciliationStatus ?? reconciliationStatus,
        Keyword: filters.Keyword ?? keyword.trim(),
        Page: filters.Page ?? report?.Pagination.Page ?? 1,
        PerPage: filters.PerPage ?? report?.Pagination.PerPage ?? 20,
      });
      setReport(data);
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "門市收款對帳載入失敗。");
      if (String(error instanceof Error ? error.message : "").includes("登入")) navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load({ Page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void load({ Page: 1 });
  }

  function handleReset() {
    setDateFrom(defaultDateFrom);
    setDateTo(defaultDateTo);
    setLocationIds([]);
    setPaymentMethod("");
    setReconciliationStatus("");
    setKeyword("");
    void load({ DateFrom: defaultDateFrom, DateTo: defaultDateTo, LocationIds: [], PaymentMethod: "", ReconciliationStatus: "", Keyword: "", Page: 1 });
  }

  function toggleLocation(locationId: string) {
    setLocationIds((current) => {
      if (!report) return current;
      if (current.length === 0) return report.Locations.map((location) => location.LocationId).filter((id) => id !== locationId);
      return current.includes(locationId) ? current.filter((id) => id !== locationId) : [...current, locationId];
    });
  }

  function openEdit(item: AdminStorePaymentReconciliationPayment) {
    setEditing(item);
    setEditStatus(item.ReconciliationStatus === "Pending" ? "Matched" : item.ReconciliationStatus);
    setEditNote(item.ReconciliationNote || "");
    setEditAbnormal(item.IsAbnormal);
    setEditAbnormalNote(item.AbnormalNote || "");
    setEditReviewNote(item.ReviewNote || "");
  }

  async function submitReview() {
    if (!editing) return;
    setSavingId(editing.StoreSalesOrderPaymentId);
    setMessage("");
    try {
      await reviewAdminStorePayment(editing.StoreSalesOrderPaymentId, {
        ReconciliationStatus: editStatus,
        ReconciliationNote: editNote,
        IsAbnormal: editAbnormal,
        AbnormalNote: editAbnormalNote,
        ReviewNote: editReviewNote,
      });
      setEditing(null);
      setMessageTone("success");
      setMessage("付款對帳資料已更新。");
      await load();
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "付款對帳更新失敗。");
    } finally {
      setSavingId("");
    }
  }

  async function submitVoid() {
    if (!voiding) return;
    setSavingId(voiding.StoreSalesOrderPaymentId);
    setMessage("");
    try {
      await voidAdminStorePayment(voiding.StoreSalesOrderPaymentId, voidReason);
      setVoiding(null);
      setVoidReason("");
      setMessageTone("success");
      setMessage("付款流水已作廢，訂單收款狀態已重新計算。");
      await load();
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "付款作廢失敗。");
    } finally {
      setSavingId("");
    }
  }

  async function submitRefund() {
    if (!refunding) return;
    setSavingId(refunding.StoreSalesOrderPaymentId);
    setMessage("");
    try {
      await refundAdminStorePayment(refunding.StoreSalesOrderPaymentId, { Amount: refundAmount, Reason: refundReason });
      setRefunding(null);
      setRefundAmount(0);
      setRefundReason("");
      setMessageTone("success");
      setMessage("退款流水已建立，訂單收款狀態已重新計算。");
      await load();
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "退款建立失敗。");
    } finally {
      setSavingId("");
    }
  }

  async function handleExport() {
    setExporting(true);
    setMessage("");
    try {
      await exportAdminStorePaymentReconciliation({ DateFrom: report?.DateFrom || dateFrom, DateTo: report?.DateTo || dateTo, LocationIds: locationIds, PaymentMethod: paymentMethod, ReconciliationStatus: reconciliationStatus, Keyword: keyword.trim() });
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "匯出失敗，請稍後再試。");
    } finally {
      setExporting(false);
    }
  }

  const summary = report?.Summary || { PaymentCount: 0, GrossReceived: 0, RefundAmount: 0, NetReceived: 0, PendingCount: 0, ExceptionCount: 0, ReviewedCount: 0, VoidedCount: 0 };

  return (
    <AdminLayout sectionLabel="門市管理" title="門市收款對帳" user={user} onLogout={handleLogout}>
      <div className="space-y-5">
        <form className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={handleSearch}>
          <div className="grid gap-3 lg:grid-cols-2 2xl:grid-cols-[minmax(180px,220px)_minmax(180px,220px)_minmax(160px,190px)_minmax(160px,190px)_minmax(260px,1fr)_auto] 2xl:items-end">
            <Field label="起日"><input className={inputClass} type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></Field>
            <Field label="迄日"><input className={inputClass} type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></Field>
            <Field label="收款方式">
              <select className={inputClass} value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value)}>
                <option value="">全部方式</option>
                {report?.PaymentMethodOptions.map((item) => <option key={item.Key} value={item.Key}>{item.Label}</option>)}
              </select>
            </Field>
            <Field label="對帳狀態">
              <select className={inputClass} value={reconciliationStatus} onChange={(event) => setReconciliationStatus(event.target.value)}>
                <option value="">全部狀態</option>
                {report?.ReconciliationStatusOptions.map((item) => <option key={item.Key} value={item.Key}>{item.Label}</option>)}
              </select>
            </Field>
            <Field label="關鍵字"><input className={inputClass} placeholder="單號、客戶、電話、收款人、備註" value={keyword} onChange={(event) => setKeyword(event.target.value)} /></Field>
            <div className="rwd-action-grid">
              <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white disabled:opacity-60" disabled={loading} type="submit"><Search size={18} />查詢</button>
              <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" disabled={loading} onClick={handleReset} type="button"><RefreshCw size={18} />重設</button>
              <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] disabled:opacity-60" disabled={!report || exporting} onClick={handleExport} type="button"><FileSpreadsheet size={18} />{exporting ? "匯出中" : "匯出"}</button>
            </div>
          </div>
          <div className="mt-4 border-t border-[#edf0ee] pt-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-extrabold text-[#203d32]">門市篩選</p>
              <button className="rounded-md border border-[#cfd8d2] px-3 py-1.5 text-sm font-bold text-[#315746]" onClick={() => setLocationIds([])} type="button">{allSelected ? "全部門市" : `已選 ${locationIds.length} 間，改全選`}</button>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
              {report?.Locations.map((location) => {
                const checked = locationIds.length === 0 || locationIds.includes(location.LocationId);
                return (
                  <label className={`inline-flex h-11 min-w-0 items-center gap-2 rounded-md border px-3 font-bold ${checked ? "border-[#9ec9ad] bg-[#edf8f1] text-[#203d32]" : "border-[#dfe6e1] bg-white text-[#59665f]"}`} key={location.LocationId}>
                    <input className="h-4 w-4 shrink-0 accent-[#315746]" checked={checked} type="checkbox" onChange={() => toggleLocation(location.LocationId)} />
                    <span className="truncate">{location.Name}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </form>

        {message && <div className={`rounded-md border px-4 py-3 text-sm font-bold ${messageTone === "success" ? "border-[#b9ddc5] bg-[#eef8f1] text-[#245438]" : "border-[#f2b8b5] bg-[#fdebea] text-[#8a1f1f]"}`}>{message}</div>}

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard icon={<WalletCards size={20} />} label="收款金額" value={money(summary.GrossReceived)} tone="good" />
          <StatCard icon={<RotateCcw size={20} />} label="退款金額" value={money(summary.RefundAmount)} tone={summary.RefundAmount > 0 ? "warning" : "default"} />
          <StatCard icon={<CheckCircle2 size={20} />} label="淨收款" value={money(summary.NetReceived)} />
          <StatCard icon={<AlertTriangle size={20} />} label="待對帳 / 異常" value={`${summary.PendingCount} / ${summary.ExceptionCount}`} tone={summary.ExceptionCount > 0 ? "danger" : summary.PendingCount > 0 ? "warning" : "default"} />
        </div>

        <div className="grid gap-4 2xl:grid-cols-2">
          <SummaryPanel title="每日收款彙總" rows={report?.DailySummary || []} />
          <SummaryPanel title="收款方式對帳" rows={report?.MethodSummary || []} labelKey="Label" />
        </div>

        <section className="rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#edf0ee] p-4">
            <div>
              <h2 className="text-lg font-extrabold text-[#203d32]">付款流水</h2>
              <p className="mt-1 text-sm font-semibold text-[#7b877f]">共 {report?.Pagination.Total || 0} 筆；退款以負數流水保留，不刪除原收款。</p>
            </div>
          </div>
          <div className="max-h-[68vh] overflow-auto">
            <table className="w-full min-w-[1180px] border-separate border-spacing-0 text-left text-sm">
              <thead className="sticky top-0 z-40 bg-[#f3f7f4] text-[#59665f]">
                <tr>
                  <th className="whitespace-nowrap bg-[#f3f7f4] px-3 py-3">收款時間</th>
                  <th className="whitespace-nowrap bg-[#f3f7f4] px-3 py-3">訂單 / 門市</th>
                  <th className="whitespace-nowrap bg-[#f3f7f4] px-3 py-3">客戶</th>
                  <th className="whitespace-nowrap bg-[#f3f7f4] px-3 py-3">類型</th>
                  <th className="whitespace-nowrap bg-[#f3f7f4] px-3 py-3">方式</th>
                  <th className="whitespace-nowrap bg-[#f3f7f4] px-3 py-3 text-right">金額</th>
                  <th className="whitespace-nowrap bg-[#f3f7f4] px-3 py-3">對帳</th>
                  <th className="whitespace-nowrap bg-[#f3f7f4] px-3 py-3">備註</th>
                  <th className="whitespace-nowrap bg-[#f3f7f4] px-3 py-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#edf0ee]">
                {(report?.Payments || []).map((item) => (
                  <tr className={item.PaymentStatus === "Void" ? "bg-[#f7f7f7] text-[#8d9690]" : ""} key={item.StoreSalesOrderPaymentId}>
                    <td className="px-3 py-3 font-bold">{item.ReceivedAt || "-"}</td>
                    <td className="px-3 py-3">
                      <Link className="break-all font-extrabold text-[#315746] hover:underline" rel="noopener noreferrer" target="_blank" to={`/admin/store-sales-orders?Keyword=${encodeURIComponent(item.OrderNumber || "")}`}>{item.OrderNumber || "-"}</Link>
                      <p className="mt-1 text-xs font-bold text-[#7b877f]">{item.LocationName || "-"}</p>
                    </td>
                    <td className="px-3 py-3">
                      <p className="font-bold text-[#203d32]">{item.CustomerName || "-"}</p>
                      <p className="mt-1 text-xs text-[#7b877f]">{item.CustomerPhone || "-"}</p>
                    </td>
                    <td className="px-3 py-3">{item.PaymentTypeLabel || item.PaymentType}</td>
                    <td className="px-3 py-3">{item.PaymentMethodLabel || item.PaymentMethod}</td>
                    <td className={`px-3 py-3 text-right font-extrabold ${item.Amount < 0 ? "text-[#9a2d2d]" : "text-[#203d32]"}`}>{money(item.Amount)}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${statusTone(item.ReconciliationStatus, item.IsAbnormal)}`}>{item.ReconciliationStatusLabel}</span>
                      {item.PaymentStatus === "Void" && <p className="mt-1 text-xs font-bold text-[#9a2d2d]">已作廢</p>}
                    </td>
                    <td className="max-w-[260px] px-3 py-3">
                      <p className="line-clamp-2 break-words text-[#59665f]">{item.AbnormalNote || item.ReconciliationNote || item.Note || "-"}</p>
                      {item.ReviewedAt && <p className="mt-1 text-xs font-bold text-[#7b877f]">覆核：{item.ReviewedByAdminName || "-"} / {item.ReviewedAt}</p>}
                    </td>
                    <td className="px-3 py-3">
                      <div className="rwd-action-grid xl:min-w-[300px]">
                        <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746]" onClick={() => openEdit(item)} type="button"><ShieldCheck size={16} />對帳</button>
                        <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#ead8ad] bg-white px-3 text-sm font-bold text-[#8a5b14] disabled:opacity-50" disabled={item.Amount <= 0 || item.PaymentStatus === "Void"} onClick={() => { setRefunding(item); setRefundAmount(Math.max(0, item.Amount)); setRefundReason(""); }} type="button"><RotateCcw size={16} />退款</button>
                        <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#f2c9c6] bg-white px-3 text-sm font-bold text-[#9a2d2d] disabled:opacity-50" disabled={item.Amount <= 0 || item.PaymentStatus === "Void"} onClick={() => { setVoiding(item); setVoidReason(""); }} type="button"><XCircle size={16} />作廢</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-[#edf0ee] p-4">
            <Pagination disabled={loading} pagination={report?.Pagination || { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 }} onChange={(page) => void load({ Page: page })} />
          </div>
        </section>
      </div>

      {editing && (
        <Modal title={`付款對帳 #${editing.StoreSalesOrderPaymentId}`} onClose={() => setEditing(null)}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="對帳狀態">
              <select className={inputClass} value={editStatus} onChange={(event) => setEditStatus(event.target.value)}>
                {report?.ReconciliationStatusOptions.map((item) => <option key={item.Key} value={item.Key}>{item.Label}</option>)}
              </select>
            </Field>
            <label className="flex min-h-11 items-center gap-3 self-end rounded-md border border-[#dfe6e1] bg-[#fbfcfb] px-3 font-bold text-[#315746]">
              <input className="h-4 w-4 accent-[#315746]" checked={editAbnormal} type="checkbox" onChange={(event) => setEditAbnormal(event.target.checked)} />
              標記付款異常
            </label>
            <Field label="對帳備註"><input className={inputClass} value={editNote} onChange={(event) => setEditNote(event.target.value)} /></Field>
            <Field label="異常原因"><input className={inputClass} value={editAbnormalNote} onChange={(event) => setEditAbnormalNote(event.target.value)} placeholder="標記異常時必填" /></Field>
            <div className="sm:col-span-2"><Field label="主管覆核備註"><textarea className={`${inputClass} min-h-[96px] py-3`} value={editReviewNote} onChange={(event) => setEditReviewNote(event.target.value)} /></Field></div>
          </div>
          <ModalActions>
            <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={() => setEditing(null)} type="button">取消</button>
            <button className="h-10 rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={savingId === editing.StoreSalesOrderPaymentId} onClick={submitReview} type="button">儲存對帳</button>
          </ModalActions>
        </Modal>
      )}

      {voiding && (
        <Modal title={`作廢付款 #${voiding.StoreSalesOrderPaymentId}`} onClose={() => setVoiding(null)}>
          <p className="mb-3 text-sm font-semibold leading-6 text-[#59665f]">作廢後會排除這筆收款並重新計算訂單尾款；資料仍會保留供稽核。</p>
          <Field label="作廢原因"><textarea className={`${inputClass} min-h-[96px] py-3`} value={voidReason} onChange={(event) => setVoidReason(event.target.value)} placeholder="例如：重複入帳、刷卡失敗、建錯付款方式" /></Field>
          <ModalActions>
            <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={() => setVoiding(null)} type="button">取消</button>
            <button className="h-10 rounded-md bg-[#9a2d2d] px-5 font-bold text-white disabled:opacity-60" disabled={savingId === voiding.StoreSalesOrderPaymentId || voidReason.trim() === ""} onClick={submitVoid} type="button">確認作廢</button>
          </ModalActions>
        </Modal>
      )}

      {refunding && (
        <Modal title={`建立退款 #${refunding.StoreSalesOrderPaymentId}`} onClose={() => setRefunding(null)}>
          <p className="mb-3 text-sm font-semibold leading-6 text-[#59665f]">退款會新增一筆負數流水，不覆蓋原始收款。原收款：{money(refunding.Amount)}</p>
          <div className="grid gap-3 sm:grid-cols-[180px_1fr]">
            <Field label="退款金額"><input className={inputClass} min={0} max={refunding.Amount} type="number" value={refundAmount} onChange={(event) => setRefundAmount(Number(event.target.value || 0))} /></Field>
            <Field label="退款原因"><input className={inputClass} value={refundReason} onChange={(event) => setRefundReason(event.target.value)} placeholder="例如：客戶取消、商品更換差額" /></Field>
          </div>
          <ModalActions>
            <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={() => setRefunding(null)} type="button">取消</button>
            <button className="h-10 rounded-md bg-[#8a5b14] px-5 font-bold text-white disabled:opacity-60" disabled={savingId === refunding.StoreSalesOrderPaymentId || refundAmount <= 0 || refundReason.trim() === ""} onClick={submitRefund} type="button">建立退款</button>
          </ModalActions>
        </Modal>
      )}
    </AdminLayout>
  );
}

const inputClass = "h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15";

function Field({ children, label }: { children: ReactNode; label: string }) {
  return <label className="min-w-0"><span className="mb-1 block text-sm font-bold text-[#59665f]">{label}</span>{children}</label>;
}

function SummaryPanel({ labelKey = "Date", rows, title }: { labelKey?: "Date" | "Label"; rows: Array<{ Date?: string; Label?: string; Count: number; GrossReceived: number; RefundAmount: number; NetReceived: number }>; title: string }) {
  return (
    <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
      <h2 className="mb-3 font-extrabold text-[#203d32]">{title}</h2>
      {rows.length === 0 ? <p className="rounded-md border border-dashed border-[#cfd8d2] p-6 text-center text-sm font-semibold text-[#7b877f]">目前區間沒有資料。</p> : null}
      <div className="space-y-2">
        {rows.slice(0, 8).map((row) => (
          <div className="grid gap-2 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3 text-sm sm:grid-cols-[1fr_auto_auto_auto] sm:items-center" key={`${row[labelKey]}-${row.Count}`}>
            <div className="font-extrabold text-[#203d32]">{row[labelKey] || "-"}</div>
            <div className="font-bold text-[#59665f]">{row.Count} 筆</div>
            <div className="font-bold text-[#245438]">收 {money(row.GrossReceived)}</div>
            <div className="font-bold text-[#9a2d2d]">退 {money(row.RefundAmount)}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Modal({ children, onClose, title }: { children: ReactNode; onClose: () => void; title: string }) {
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-[#10251c]/35 p-4">
      <section className="max-h-[88vh] w-full max-w-[680px] overflow-y-auto rounded-md border border-[#dfe6e1] bg-white shadow-[0_20px_60px_rgba(16,37,28,.22)]">
        <div className="flex items-center justify-between gap-3 border-b border-[#edf0ee] px-4 py-3">
          <h2 className="font-extrabold text-[#203d32]">{title}</h2>
          <button className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#cfd8d2] text-[#315746]" onClick={onClose} type="button">×</button>
        </div>
        <div className="p-4">{children}</div>
      </section>
    </div>
  );
}

function ModalActions({ children }: { children: ReactNode }) {
  return <div className="rwd-action-grid mt-4 sm:flex sm:justify-end">{children}</div>;
}
